import type { PropertyName } from "@frui.ts/helpers/dist";
import { bound } from "@frui.ts/helpers/dist";
import { Router, watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, validateAll } from "@frui.ts/validation/dist";
import type { EntityNames } from "config";
import { Modules } from "config";
import BankAccountBlacklist, { BankAccountBlacklistValidationRules } from "entities/bankAccountBlacklist";
import { BankAccountFormatType } from "entities/bankAccountFormatType";
import type { interfaces } from "inversify";
import { computed, reaction } from "mobx";
import BankAccountBlacklistRepository from "repositories/bankAccountBlacklistRepository";
import EventBus, { blacklistedAccountsChanged } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import RefundDisapprovalService from "../../../services/refundDisapprovalService";
import { serializeAccountNumber } from "../../../utils/filters";
import AlertsViewModel from "../../alertsViewModel";
import EditableDetailViewModel from "../../editableDetailViewModel";
import type BankAccount from "entities/bankAccount";
import { BankAccountValidationRules } from "entities/bankAccount";
import { detectAccountType, setAccountNumberAndCodeByType } from "@edaz/utils/bankAccount";

@Router.registerRoute({ name: Router.Self, route: ":bankAccountBlacklistId" })
export default class BankAccountBlacklistDetailViewModel extends EditableDetailViewModel<BankAccountBlacklist> {
  readonly modelName = "bankAccountBlacklist";
  private unsubscriptions: Array<() => void> = [];

  @computed
  get accountFormatTypes() {
    return this.optionsService.internalEnumOptions(BankAccountFormatType);
  }

  constructor(
    protected itemId: string,
    protected repository: BankAccountBlacklistRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    private optionsService: OptionsService,
    private refundsDisapprovalService: RefundDisapprovalService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(itemId);
  }

  @bound
  @watchBusy
  async save() {
    if (validateAll([this.item, this.item.bankAccount]) && this.isEditable) {
      this.item.bankAccount.accountNumber = serializeAccountNumber(this.item.bankAccount.accountNumber) ?? "";
      const confirmed = await this.refundsDisapprovalService.disapproveWithConfirmation(this.item);
      if (confirmed) {
        super.save();
      }
    }
  }

  protected async loadDetail(): Promise<BankAccountBlacklist> {
    const item = await this.repository.get(this.itemId);
    this.recognizeAccountFormatType(item);
    attachAutomaticValidator(item, BankAccountBlacklistValidationRules);
    attachAutomaticValidator(item.bankAccount, BankAccountValidationRules);

    return item;
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.unsubscriptions.push(reaction(() => this.item.bankAccount._formatType, this.onFormatTypeChanged));
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  updateData(): Promise<BankAccountBlacklist> {
    return this.repository.update(this.item);
  }

  protected onUpdated() {
    super.onUpdated();
    this.eventBus.publish(
      blacklistedAccountsChanged({
        bankAccount: { accountNumber: this.item.bankAccount.accountNumber, bank: this.item.bankAccount.bank },
      })
    );
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `bankAccountBlacklists.${code}`, size);
  }

  translateSubAttribute(entityName: "bankAccount", attribute: PropertyName<BankAccount>): string;
  translateSubAttribute(entityName: EntityNames, attribute: string) {
    return this.localizationService.translateAttribute(entityName, attribute);
  }

  @bound
  onHide() {
    this.requestClose();
  }

  @bound
  private recognizeAccountFormatType(item: BankAccountBlacklist) {
    item.bankAccount._formatType = detectAccountType(item.bankAccount.accountNumber);
  }

  @bound
  onInvalidAccountNumberPasted(value: string, event: ClipboardEvent) {
    this.recognizeAccountFormatType(this.item);
    setAccountNumberAndCodeByType(value, this.item.bankAccount._formatType, this.item.bankAccount);
    // We need to prevent default beacuse we're setting the input value above
    event.preventDefault();
  }

  @bound
  private onFormatTypeChanged() {
    this.item.bankAccount.accountNumber = "";
    this.item.bankAccount.bank = "";
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new BankAccountBlacklistDetailViewModel(
        itemId,
        container.get(BankAccountBlacklistRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(OptionsService),
        container.get(RefundDisapprovalService),
        container.get(AlertsViewModel)
      );
  }
}
